import { useApolloClient } from '@apollo/client';
import { useEffect, useMemo, useRef, useState } from 'react';
import { chunk } from 'lodash-es';
import { addError } from '@shared/utils/logger';
import { isBotClient } from '@shared/utils/botDetection/isBotClient';
import { useSearchAccommodationsQueryMetadataPayload } from '@shared/hooks/useSearchAccommodationsQueryMetadataPayload';
import { DateResolutionStrategy } from '@graphql/generated-base';
import type { AccommodationCatalogUnitsOccupancyInput, SearchExtendedAccommodationPropertiesV2Payload, TravelPlacePricingAndBookingDetailsPayload } from '@graphql/generated-base';
import {
  AccommodationsConsoleBatchedLivePricingDocument,
  type AccommodationConsoleJoyPlaceLivePricingFragment,
  type AccommodationsConsoleBatchedLivePricingQuery,
  type AccommodationsConsoleBatchedLivePricingQueryVariables,
} from '../AccommodationsConsole.graphql.generated';

/**
 * Hotels per pricing request. The server cost is roughly a fixed ~3.5s vendor floor plus ~29ms per
 * hotel, so a batch of 40 costs about the same as a batch of 20 — the floor dominates. 40 also covers
 * the first rendered page (`PAGE_SIZE` is 30 in usePagination), so batch one is what the user sees.
 */
const DEFAULT_BATCH_SIZE = 40;

/**
 * Requests in flight at once. Bounded because every batch fans out to the vendors server-side; an
 * unbounded burst risks provider rate limits. Tunable from the flag payload without a deploy.
 */
const DEFAULT_MAX_CONCURRENCY = 4;

type BatchedLivePricingState = Readonly<{
  pricingByJoyPlaceId: ReadonlyMap<string, AccommodationConsoleJoyPlaceLivePricingFragment>;
  settledJoyPlaceIds: ReadonlySet<string>;
  isSettling: boolean;
  failedBatchCount: number;
  batchCount: number;
  batchSize: number;
  /** Relative to `fanOutStartedAt`, which is after the snapshot search settled. For a figure comparable
   *  with the control arm, measure from search start instead — see `useAccommodationsConsoleListings`.
   *  `msToFirstBatch` is the first batch that priced at least one hotel; null if none did. */
  msToFirstBatch: number | null;
  msToAllBatches: number | null;
  /** Epoch ms at which this fan-out began. Null while idle. */
  fanOutStartedAt: number | null;
}>;

type BatchedLivePricingResult = BatchedLivePricingState &
  Readonly<{
    /** False when the payload cannot be built (no dates/occupancies/currency yet). The caller then keeps
     *  the whole-list refetch, so a user is never left with no live prices at all. */
    canFetch: boolean;
    /** The payload is only waiting on search metadata, so it is about to become buildable. The caller
     *  holds the refetch rather than firing it and then the batches too. */
    isPreparingPayload: boolean;
  }>;

const EMPTY_STATE: BatchedLivePricingState = {
  pricingByJoyPlaceId: new Map(),
  settledJoyPlaceIds: new Set(),
  isSettling: false,
  failedBatchCount: 0,
  batchCount: 0,
  batchSize: 0,
  msToFirstBatch: null,
  msToAllBatches: null,
  fanOutStartedAt: null,
};

type UseBatchedLivePricingArgs = Readonly<{
  /** joyPlaceIds in display order, so batch one covers the tiles above the fold. */
  joyPlaceIds: ReadonlyArray<string>;
  /** The payload the snapshot search ran with. Only `currencyCode` is read from it — it is the one
   *  field present on both search branches, and reading it here keeps live prices in the same currency
   *  as the snapshot prices they are patched onto. */
  regionalHotelsPayload: Maybe<SearchExtendedAccommodationPropertiesV2Payload>;
  /** From console state, NOT from the search payload. The event-defaulted search sends a deliberately
   *  thin payload (no dates, no occupancies) and lets the server derive them from the event — so
   *  reading them from the payload leaves that branch unpriceable. Console state always has them, and
   *  derives them from the same `info.finalizedDate` with the same offsets the server uses
   *  (`calculateStayDates` mirrors `calculateCheckDate`: eventDate -1 / +1), so the dates we price with
   *  are the dates the tiles were searched with. */
  searchDates: Maybe<{ checkin: string; checkout: string }>;
  occupancies: Maybe<AccommodationCatalogUnitsOccupancyInput>;
  skip: boolean;
  batchSize?: number;
  maxConcurrency?: number;
}>;

/**
 * Fetches live vendor pricing for already-rendered listings, by id, in bounded-parallel batches.
 *
 * Replaces the single realtime refetch that re-ran the whole search and swapped the list wholesale.
 * `travelPlacesPricingAndBookingDetails` is the only id-targetable pricing query — the search op has
 * no id-list input — and server-side it calls the same `getRatesAndRedirects` as the search path, so
 * price comparison, provider selection and redirect URLs are identical to what the swap produced.
 *
 * Results are reported per batch so prices stream into the list instead of arriving in one step. The
 * caller merges them with `applyLivePricingToListings`.
 */
export const useBatchedLivePricing = ({
  joyPlaceIds,
  regionalHotelsPayload,
  searchDates,
  occupancies,
  skip,
  batchSize = DEFAULT_BATCH_SIZE,
  maxConcurrency = DEFAULT_MAX_CONCURRENCY,
}: UseBatchedLivePricingArgs): BatchedLivePricingResult => {
  const client = useApolloClient();
  const { isPreparingSearchMetadataPayload, searchMetadata } = useSearchAccommodationsQueryMetadataPayload();
  const [state, setState] = useState<BatchedLivePricingState>(EMPTY_STATE);

  // Bumped on every fan-out. A batch that resolves after the user changed dates or location carries a
  // stale generation and is dropped — without this it would patch confidently wrong prices onto the new
  // result set, and on a date change the joyPlaceIds overlap almost completely so nothing would look off.
  // An AbortController cannot do this job here: createAuthLink returns its own `fetchOptions.signal`
  // from setContext, which shallow-replaces any signal a caller puts on the context.
  const generationRef = useRef(0);

  const customSearchParams = regionalHotelsPayload?.customSearchParams;

  // Everything except the ids. Dates and occupancies come from console state (see the arg docs);
  // currency comes from the search payload rather than from a hook, because
  // useAccommodationPlacesPricing gates currency behind `localeBasedHotelCurrency` while
  // useRegionalHotels does not — reading it from a hook here would render live prices in a different
  // currency from the snapshot prices sitting beside them in the same list.
  const basePayload: Omit<TravelPlacePricingAndBookingDetailsPayload, 'joyPlaceIds'> | undefined = useMemo(() => {
    if (isPreparingSearchMetadataPayload || !searchDates?.checkin || !searchDates?.checkout || !occupancies || !customSearchParams?.currencyCode) {
      return undefined;
    }
    return {
      currencyCode: customSearchParams.currencyCode,
      // A single day per bound, matching what the console searched — the range form exists for flexible
      // date search, which this surface does not use.
      dates: {
        checkIn: { low: searchDates.checkin, high: searchDates.checkin },
        checkOut: { low: searchDates.checkout, high: searchDates.checkout },
      },
      occupancies,
      metadata: searchMetadata,
      // Explicit: the metadata carries an eventId, and USE_EVENT_DATES would price the couple's wedding
      // dates instead of the dates the guest actually searched.
      dateResolutionStrategy: DateResolutionStrategy.USE_PAYLOAD_DATES,
    };
  }, [isPreparingSearchMetadataPayload, customSearchParams?.currencyCode, searchDates?.checkin, searchDates?.checkout, occupancies, searchMetadata]);

  const idsKey = joyPlaceIds.join(',');
  const payloadKey = basePayload ? JSON.stringify(basePayload) : null;
  const fanOutKey = payloadKey && idsKey ? `${payloadKey}::${idsKey}` : null;

  const prevFanOutKey = useRef<string | null>(null);

  useEffect(() => {
    if (skip || !fanOutKey || !basePayload || joyPlaceIds.length === 0 || isBotClient()) {
      return;
    }
    if (fanOutKey === prevFanOutKey.current) {
      return;
    }
    prevFanOutKey.current = fanOutKey;

    const generation = ++generationRef.current;
    const isCurrent = () => generationRef.current === generation;

    // Deduplicated: `settled` is a Set, so a repeated id would otherwise keep `allDone` false forever.
    const uniqueIds = [...new Set(joyPlaceIds)];
    const uniqueIdCount = uniqueIds.length;
    const batches = chunk(uniqueIds, batchSize);
    const startedAt = Date.now();

    setState({ ...EMPTY_STATE, isSettling: true, batchCount: batches.length, batchSize, fanOutStartedAt: startedAt });

    const accumulated = new Map<string, AccommodationConsoleJoyPlaceLivePricingFragment>();
    const settled = new Set<string>();
    let failedBatchCount = 0;
    let firstBatchAt: number | null = null;

    /** Resolves to the number of hotels the batch actually priced. */
    const runBatch = async (ids: ReadonlyArray<string>): Promise<number> => {
      let pricedCount = 0;
      try {
        const result = await client.query<AccommodationsConsoleBatchedLivePricingQuery, AccommodationsConsoleBatchedLivePricingQueryVariables>({
          query: AccommodationsConsoleBatchedLivePricingDocument,
          variables: { payload: { ...basePayload, joyPlaceIds: [...ids] } },
          // Prices must never be served from cache, and a partial response is still worth patching.
          fetchPolicy: 'no-cache',
          errorPolicy: 'all',
        });
        const rows = result.data?.travelPlacesPricingAndBookingDetails?.joyPlaces ?? [];
        for (const entry of rows) {
          if (entry.joyPlace?.id) {
            accumulated.set(entry.joyPlace.id, entry);
            if (entry.pricingInfo) {
              pricedCount += 1;
            }
          }
        }
        // `errorPolicy: 'all'` turns a GraphQL error into a resolved result rather than a throw. With no
        // rows it is a failed batch in every sense that matters, so count and log it like a network error.
        if (result.errors?.length && rows.length === 0) {
          failedBatchCount += 1;
          addError(new Error(result.errors[0].message), {
            source: 'Batched live pricing batch returned only errors',
            context: 'useBatchedLivePricing',
            joyPlaceIdCount: ids.length,
            errorCount: result.errors.length,
          });
        }
      } catch (error) {
        // One batch failing must not block the rest. The hotels it covered keep their snapshot prices.
        failedBatchCount += 1;
        addError(error as Error, {
          source: 'Batched live pricing batch failed',
          context: 'useBatchedLivePricing',
          joyPlaceIdCount: ids.length,
        });
      } finally {
        // Settled either way — the caller stops showing a skeleton for these and keeps the snapshot price.
        ids.forEach(id => settled.add(id));
      }
      return pricedCount;
    };

    // A shared cursor rather than awaiting each group: with a ~3.5s floor per request, a group barrier
    // would hold the whole tail behind the slowest member of each group.
    let cursor = 0;
    const worker = async () => {
      while (cursor < batches.length) {
        const index = cursor++;
        const pricedCount = await runBatch(batches[index]);
        if (!isCurrent()) return;
        // The first batch that put a live price on screen — not merely the first to settle. A fast
        // failed or empty batch must not register as "first live price", or it flatters the readout.
        if (firstBatchAt === null && pricedCount > 0) {
          firstBatchAt = Date.now();
        }
        const allDone = settled.size >= uniqueIdCount;
        setState({
          pricingByJoyPlaceId: new Map(accumulated),
          settledJoyPlaceIds: new Set(settled),
          isSettling: !allDone,
          failedBatchCount,
          batchCount: batches.length,
          batchSize,
          msToFirstBatch: firstBatchAt === null ? null : firstBatchAt - startedAt,
          msToAllBatches: allDone ? Date.now() - startedAt : null,
          fanOutStartedAt: startedAt,
        });
      }
    };

    void Promise.all(Array.from({ length: Math.min(maxConcurrency, batches.length) }, worker));
  }, [skip, fanOutKey, basePayload, joyPlaceIds, batchSize, maxConcurrency, client]);

  // Unmount stops the pool: without it `isCurrent()` stays true, so the workers would keep sending the
  // remaining batches to the vendors and setState on an unmounted component. Mount-only on purpose — the
  // fan-out effect also re-runs on `joyPlaceIds`/`basePayload` identity changes with an unchanged key,
  // and a cleanup there would kill a live fan-out without restarting it.
  useEffect(
    () => () => {
      generationRef.current += 1;
    },
    [],
  );

  // A new search invalidates any in-flight fan-out; report an idle state immediately rather than
  // leaving the previous search's prices on screen while the next one is still being assembled.
  useEffect(() => {
    if (skip) {
      generationRef.current += 1;
      prevFanOutKey.current = null;
      setState(EMPTY_STATE);
    }
  }, [skip]);

  // Derived rather than stored: the caller needs this on the very first render to decide whether to
  // keep the fallback refetch alive, before any effect has run.
  return useMemo(
    () => ({
      ...state,
      canFetch: !!basePayload && joyPlaceIds.length > 0,
      isPreparingPayload: isPreparingSearchMetadataPayload,
    }),
    [state, basePayload, joyPlaceIds.length, isPreparingSearchMetadataPayload],
  );
};
